/**
 * useProductsQuery.ts — fetch state for the Products tab (story E0-BO-NUM6).
 *
 * Loads the full catalogue (`GET /api/config/products`, Story N3 AC1) and the
 * configured tenant's availability rows (`GET /api/config/products/availability`,
 * N3 AC2/AC3) together — the toggle grid (N4) needs both to render a single
 * pass. Race-safety mirrors `useLeadsQuery`: a monotonic request id means only
 * the latest `reload` may commit.
 *
 * Failure posture (N4 AC4): a 401/403 on either call → `unauthorized`; any
 * other fault → `error` with the already-loaded rows cleared, never a blank
 * page that pretends to be empty.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type { ProductConfig, TenantProductAvailability } from '../../api/types';
import floating from '../../lib/floating';

export type ProductsPhase = 'loading' | 'ready' | 'unauthorized' | 'error';

export interface ProductsQueryState {
  catalogue: ProductConfig[];
  /** ALL tenant_products rows, including soft-disabled ones — see client.ts. */
  availability: TenantProductAvailability[];
  phase: ProductsPhase;
  reload: () => Promise<void>;
}

export const useProductsQuery = (apiClient: ApiClient): ProductsQueryState => {
  const [catalogue, setCatalogue] = useState<ProductConfig[]>([]);
  const [availability, setAvailability] = useState<TenantProductAvailability[]>([]);
  const [phase, setPhase] = useState<ProductsPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const [catalogueResult, availabilityResult] = await Promise.all([
        apiClient.getProductCatalogue(),
        apiClient.getTenantProductAvailability(),
      ]);
      if (!isCurrent()) return;
      setCatalogue(catalogueResult);
      setAvailability(availabilityResult);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      setCatalogue([]);
      setAvailability([]);
      setPhase('error');
    }
  }, [apiClient]);

  useEffect(() => {
    floating(reload());
  }, [reload]);

  return { catalogue, availability, phase, reload };
};
